import React, { useEffect, useRef, useState } from "react";

// Hooks
import { useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "app/hooks";
import useRedirectOnNoAdminPermission from "utils/hooks/useRedirectOnNoAdminPermission";

// Store
import { setToastConfig, toggleLoader } from "store/slice/UI";

import { SelectChangeEvent } from "@mui/material";
import { OrderTableBy, OrderTable } from "components/SortMenu/SortMenu";

// Service
import {
    getUsersManagement,
    getTeams,
    addNewTeam,
    addUserToArchived,
    removeUserFromArchived,
    getPendingInvitations,
    removeTeamFromUser,
    updateUserFunction,
    updateUserRoles,
} from "services/user_management";

// Utils
import { debounce, isEmpty, isEqual } from "lodash";
import { differenceBy } from "lodash";
import {
    generateID,
    getPermissionNameFromRolesList,
    getUserRolesByPermissions,
    getUserRolesWithCustomPlaceholder,
    snakeCaseToCapitalizedString,
    useAuth,
} from "utils";

// Enum
import { urlPaths } from "enums/urlPaths";

// Store
import {
    fetchPermissionsRefBundleThunk,
    fetchPermissionsRefThunk,
    getPermissionsRefBundle,
} from "store/slice/sysadmin/permissions/permissionsSlice";
import { getUserOrgs } from "store/slice/sysadmin/userOrgs/userOrgsSlice";

// Config
import { tableHeaderConfig } from "./config";

// Abilities
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";

// Types
import { Team, TUsersFiltersPayload, userFunctions } from "services/user_management/user_management.types";
import { TPendingInvitation } from "pages/HubManagement/components/InviteUsers/InviteUsersModal.types";
import { Pagination, TUserRole } from "types";
import { initialPagination } from "constants/constants";
import { convertUsers, TConvertedUser } from "./adaptares/converter";
import { Role } from "services";
import { RequestInfo } from "store/slice/store.types";

type Filters = { filter_name: string; filter_functions: string[]; filter_teams: string[] };
export type TUsersData = {
    data: TConvertedUser[];
    meta: Pagination;
    status: RequestInfo;
};

const FILTERS_INITIAL_VALUE = { filter_name: "", filter_functions: ["placeholder"], filter_teams: ["placeholder"] };
const DEFAULT_SORT = { columnId: "name", direction: "asc" } as OrderTableBy;
const DEFAULT_PAGE_SIZE = 24;

const useUserManagement = (): {
    users: TUsersData;
    archivedUsers: TConvertedUser[];
    teamsData: Team[];
    userFunctions: TUserRole[];
    filters: Filters;
    inputName: string;
    usersSort: OrderTableBy;
    paginationTextInputRef: React.MutableRefObject<any>;
    tableHeader: typeof tableHeaderConfig;
    hidableColumns: typeof tableHeader;
    visibleColumns: (string | number)[];
    disableAll: boolean;
    disableClearAll: boolean;
    shouldOpenArchiveModal: boolean;
    currentPage: number | string;
    shouldOpenTeamsModal: boolean;
    twoUsersSelected: boolean;
    areSomeUsersChecked: boolean;
    allUsersChecked: boolean;
    selectedUserName: string | null;
    activeTab: number;
    inviteModalOpen: boolean;
    pendingInvitations: TPendingInvitation[];
    orgId: number | undefined;
    fetchUsers: (params: Partial<TUsersFiltersPayload>) => void;
    handleSort: (direction: OrderTable, columnId: string | number) => void;
    handleChangeFilters: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string[]>
    ) => void;
    handleClearFilters: (filterName?: "filter_functions" | "filter_teams" | "placeholder") => void;
    handleArchiveUser: () => void;
    handleSelectFunction: (event: SelectChangeEvent, userId: number, isArchivedUser?: boolean) => Promise<void>;
    toggleCheckUser: (event: React.ChangeEvent<HTMLInputElement>, userId: number) => void;
    toggleCheckAllUsers: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleToggleColumn: (ev: React.MouseEvent<HTMLLIElement, MouseEvent>, itemId: string | number) => void;
    handleOpenArchiveModal: (event: React.MouseEvent<HTMLElement, MouseEvent>, userData: any) => void;
    handleCloseArchiveModal: (reason: "cancel" | "confirm") => void;
    handleUnarchiveUser: (userData: TConvertedUser) => void;
    handlePageChange: (newPage: number) => void;
    handleOpenTeamsModal: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, userID?: number) => void;
    handleCloseTeamsModal: (reason: "cancel" | "confirm") => void;
    handleConfirmAddTeam: (userIds: number[], teamsNames: string[]) => Promise<void>;
    handleChipSelect: (value: string) => void;
    handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleOnBlur: () => void;
    handleOnKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    setSelectedUserForTeam: (userId: number) => void;
    handleOpenInvitationModal: () => void;
    handleInviteSelectedUsers: () => void;
    closeInviteModal: () => void;
    handleRemoveTeamFromUser: (user_id: number, teamName: string) => Promise<void>;
} => {
    const dispatch = useAppDispatch();
    const { sessionData } = useAuth();
    const intl = useIntl();

    // If user changes current hub and doesn't have permissions, redirect somewhere safe
    useRedirectOnNoAdminPermission("SYS_ADMIN_VIEW_USER_MANAGEMENT", urlPaths.PaceLabsDashboard);

    const ability = useAbility(AbilityContext);
    const canInviteUsers = ability.can("see", "SYS_ADMIN_INVITE_USERS");

    const selectedOrg = sessionData?.authn.user.org_selected;
    const orgId = selectedOrg?.id;
    const groupId = selectedOrg?.group_id;

    const [users, setUsers] = useState<TUsersData>({
        meta: initialPagination,
        data: [],
        status: "pristine",
    });

    const [archivedUsers, setArchivedUsers] = useState<TConvertedUser[]>([]);
    const [selectedUserName, setSelectedUserName] = useState<string | null>(null);
    const [inputName, setInputName] = useState("");
    const [filters, setFilters] = useState<Filters>(FILTERS_INITIAL_VALUE);
    const previousFilters = useRef(FILTERS_INITIAL_VALUE);
    const [usersSort, setUsersSort] = useState<OrderTableBy>(DEFAULT_SORT);
    const [tableHeader, setTableHeader] = useState(tableHeaderConfig);

    const [userFunctions, setUserFunctions] = useState<TUserRole[]>([]);
    const [teamsData, setTeamsData] = useState<Team[]>([]);

    // User roles
    const { data: permissionsBundle } = useAppSelector(getPermissionsRefBundle);
    const { data: userOrgs } = useAppSelector(getUserOrgs);
    const selectedClientHub = userOrgs.find((org) => org.id === orgId);

    // Pagination
    const paginationTextInputRef = useRef<any>(null);
    const [currentPage, setCurrentPage] = useState<number | string>(1);
    const previousPageValue = useRef<number | string>(1);

    const hidableColumns = tableHeader.filter((cell) => cell.hasOwnProperty("isHidden"));
    const visibleColumns = hidableColumns.filter((cell) => cell.isHidden).map((col) => col.id);

    const disableAll = !users.data.length;
    const shouldOpenArchiveModal = users.data.some((user) => user.selected);
    const disableClearAll = Boolean(
        !Object.values(filters)
            .flat()
            .filter((el) => el !== "placeholder" && el !== "").length
    );
    const [shouldOpenTeamsModal, setShouldOpenTeamsModal] = useState<boolean>(false);
    const twoUsersSelected = users.data.filter((user) => user.checked).length > 1;
    const areSomeUsersChecked = Boolean(
        users.data.filter((user) => user.checked).length > 0 &&
            users.data.filter((user) => user.checked).length < users.data.length
    );
    const allUsersChecked = users.data.every((user) => user.checked);

    // Invitations
    const [activeTab, setActiveTab] = useState<number>(0);
    const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);
    const [pendingInvitations, setPendingInvitations] = useState<TPendingInvitation[]>([]);

    const fetchPendingInvitations = async () => {
        try {
            const { data } = await getPendingInvitations(Number(orgId));

            const pendingWithoutInvite: TPendingInvitation[] = data.pending_users_without_invite.map((user) => ({
                id: user.id,
                recipient_name: user.name,
                recipient_email: user.email,
            }));

            setPendingInvitations([...data.org_invites_sent, ...pendingWithoutInvite]);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchTeams = async () => {
        try {
            const { data } = await getTeams(true);
            const teamsWithIds = data.teams.map((team) => ({
                ...team,
                id: generateID(),
                name: team.name,
            }));

            setTeamsData([{ id: "placeholder", name: "All (default)", selected: true }, ...teamsWithIds]);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchUsers = async (params: Partial<TUsersFiltersPayload>) => {
        if ((isEqual(filters, previousFilters.current) && isEmpty(params)) || !groupId) return;

        const isDefaultFunction = params.filter_functions?.some((val) => val === "placeholder");
        const isDefaultTeams = params.filter_teams?.some((val) => val === "placeholder");

        const requestPayload = {
            ...(params.filter_name ? { filter_name: params.filter_name } : {}),
            ...(isDefaultFunction ? {} : { filter_functions: params.filter_functions }),
            ...(isDefaultTeams ? {} : { filter_teams: params.filter_teams }),
            ...(params.order_by ? { order_by: params.order_by } : {}),
            page_no: params?.page_no ? params.page_no : 1,
            page_size: DEFAULT_PAGE_SIZE,
            filter_enabled: true,
            only_user_org_selected: true,
        };

        try {
            setUsers((users) => ({ ...users, status: "fetching" }));
            const { data } = await getUsersManagement(requestPayload);
            setUsers((users) => ({
                ...users,
                meta: data.meta,
                data: convertUsers(data.results, groupId),
            }));
            previousFilters.current = isEmpty(params) ? filters : FILTERS_INITIAL_VALUE;
        } catch (error) {
            console.error(error);
        } finally {
            setUsers((users) => ({ ...users, status: "done" }));
        }
    };

    const delayedFilterUserName = useRef(
        debounce((value: string, filters: Filters, usersSort: OrderTableBy) => {
            fetchUsers({ ...filters, filter_name: value, order_by: `${usersSort.columnId},${usersSort.direction}` });
        }, 500)
    ).current;

    useEffect(() => () => delayedFilterUserName.cancel(), [delayedFilterUserName]);

    const handleChangeFilters = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string[]>
    ) => {
        setCurrentPage(1);

        // Filter by user name
        if ((event as any)?.nativeEvent?.target?.name === "user-filter") {
            const newValue = (event as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target.value;
            setInputName(newValue);
            setFilters((currentVal) => ({ ...currentVal, filter_name: newValue }));
            delayedFilterUserName(newValue, filters, usersSort);
        }

        // Filter by roles or teams
        if (event.target.value && event.target.name !== "user-filter") {
            const filterName = event.target.name;
            let newValue = event.target.value as string[];

            if ((newValue.length > 1 && newValue.at(-1) === "placeholder") || !newValue.length) {
                handleClearFilters(filterName as Parameters<typeof handleClearFilters>[0]);
                return;
            }

            newValue = newValue.filter((value) => value !== "placeholder");
            const newFilters = { ...filters, [filterName]: newValue };

            setFilters(newFilters);
            fetchUsers({ ...newFilters, order_by: `${usersSort.columnId},${usersSort.direction}` });
        }
    };

    const handleSelectFunction = async (event: SelectChangeEvent, userId: number, isArchivedUser = false) => {
        if (!groupId) return;

        const roles = userFunctions.filter((role) => role.id === event.target.value)[0].userRoles as Role[];
        const userArray = isArchivedUser ? archivedUsers : users.data;
        const userData = userArray.filter((user) => user.id === userId)[0];

        if (isArchivedUser) {
            setArchivedUsers((prevUsers) =>
                prevUsers.map((user) => ({
                    ...user,
                    function: userId === user.id ? event.target.value : user.function,
                    roles: userId === user.id ? roles : user.roles,
                    status: userId === user.id ? "saving" : user.status,
                }))
            );
        } else {
            setUsers((prevUsers) => ({
                ...prevUsers,
                data: prevUsers.data.map((user) => ({
                    ...user,
                    function: userId === user.id ? event.target.value : user.function,
                    roles: userId === user.id ? roles : user.roles,
                    status: userId === user.id ? "saving" : user.status,
                })),
            }));
        }

        try {
            await updateUserRoles(userId, groupId, { roles: roles.map((role) => role.name) });
            await updateUserFunction(userId, groupId, { function: event.target.value as userFunctions });
            dispatch(
                setToastConfig({
                    title: intl.formatMessage({ id: "userManagement.changeRole.toast.title" }),
                    content: intl.formatMessage(
                        { id: "userManagement.changeRole.toast.content" },
                        { userName: userData.name, newRole: snakeCaseToCapitalizedString(event.target.value) }
                    ),
                })
            );

            if (isArchivedUser) {
                setArchivedUsers((prevUsers) =>
                    prevUsers.map((user) => ({
                        ...user,
                        status: userId === user.id ? "done" : user.status,
                    }))
                );
            } else {
                setUsers((prevData) => ({
                    ...prevData,
                    data: prevData.data.map((user) => ({
                        ...user,
                        status: userId === user.id ? "done" : user.status,
                    })),
                }));
            }
        } catch (error) {
            console.error(error);

            if (isArchivedUser) {
                setArchivedUsers((prevUsers) =>
                    prevUsers.map((user) => ({
                        ...user,
                        function: userId === user.id ? userData.function : user.function,
                        roles: userId === user.id ? userData.roles : user.roles,
                        status: userId === user.id ? "error" : user.status,
                    }))
                );
            } else {
                setUsers((prevData) => ({
                    ...prevData,
                    data: prevData.data.map((user) => ({
                        ...user,
                        function: userId === user.id ? userData.function : user.function,
                        roles: userId === user.id ? userData.roles : user.roles,
                        status: userId === user.id ? "error" : user.status,
                    })),
                }));
            }
        }
    };

    const handleClearFilters = (filterName?: "filter_functions" | "filter_teams" | "placeholder") => {
        if (filterName === "placeholder") {
            setInputName("");
            setCurrentPage(1);
            setFilters(FILTERS_INITIAL_VALUE);
            fetchUsers({
                ...FILTERS_INITIAL_VALUE,
                order_by: `${DEFAULT_SORT.columnId},${DEFAULT_SORT.direction}`,
            });
            return;
        }
        if (filterName) {
            const newFilters = { ...filters, [filterName]: ["placeholder"] };
            setFilters(newFilters);
            fetchUsers({ ...newFilters, order_by: `${usersSort.columnId},${usersSort.direction}` });
        }
    };

    const handleSort = (direction: OrderTable, columnId: string | number) => {
        setUsersSort({ columnId, direction });
        fetchUsers({
            ...filters,
            page_size: users.meta.page_size,
            page_no: typeof currentPage === "string" ? parseInt(currentPage || "1") : currentPage,
            order_by: `${columnId},${direction}`,
        });
    };

    const handleArchiveUser = async () => {
        const userToArchive = users.data.find((user) => user.selected);

        if (!userToArchive) return;

        try {
            await addUserToArchived(userToArchive.id);

            // Could be multiple users?
            dispatch(
                setToastConfig({
                    title: intl.formatMessage({ id: "userManagement.archiveUser.toast.title" }),
                    content: intl.formatMessage(
                        { id: "userManagement.archiveUser.toast.content" },
                        { userName: userToArchive.name }
                    ),
                })
            );
            handleCloseArchiveModal("confirm");
        } catch (error) {
            console.error(error);
        }
    };

    const toggleCheckUser = (event: React.ChangeEvent<HTMLInputElement>, userId: number) => {
        const isChecked = event.target.checked;
        setUsers((prevState) => ({
            ...prevState,
            data: prevState.data.map((member) => ({
                ...member,
                checked: member.id === userId ? isChecked : member.checked,
                selectedForTeam: member.id === userId ? isChecked : member.selectedForTeam,
            })),
        }));
    };

    const toggleCheckAllUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUsers((prevState) => ({
            ...prevState,
            data: prevState.data.map((member) => ({
                ...member,
                checked: event.target.checked,
                selectedForTeam: true,
            })),
        }));
    };

    const setSelectedUserForTeam = (userId: number) => {
        setUsers((prevState) => ({
            ...prevState,
            data: prevState.data.map((user) => ({
                ...user,
                selectedForTeam: user.id === userId ? true : false,
            })),
        }));
    };

    const handleOpenArchiveModal = (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        userData: typeof users.data[0]
    ) => {
        event.stopPropagation();
        setUsers((prevState) => ({
            ...prevState,
            data: prevState.data.map((user) => ({
                ...user,
                selected: userData.id === user.id,
            })),
        }));
        setSelectedUserName(userData.name);
    };

    const handleCloseArchiveModal = (reason?: "cancel" | "confirm") => {
        if (reason === "cancel") {
            setUsers((prevState) => ({
                ...prevState,
                data: prevState.data.map((user) => ({
                    ...user,
                    selected: false,
                })),
            }));
        }

        if (reason === "confirm") {
            const usersToArchive = users.data
                .filter((user) => user.selected)
                .map((user) => ({ ...user, roleId: "placeholder" }));

            setArchivedUsers((archived) => [
                ...archived,
                ...usersToArchive.map((user) => ({ ...user, selected: false })),
            ]);

            setUsers((prevState) => ({
                ...prevState,
                data: differenceBy(prevState.data, usersToArchive, "selected"),
            }));
        }
    };

    const handleToggleColumn = (ev: React.MouseEvent<HTMLLIElement, MouseEvent>, itemId: string | number) => {
        setTableHeader((prevVal) =>
            prevVal.map((cell) => {
                if (cell.hasOwnProperty("isHidden") && cell.id === itemId) {
                    return { ...cell, isHidden: !cell.isHidden };
                }
                return cell;
            })
        );
    };

    const handleUnarchiveUser = async (userData: TConvertedUser) => {
        try {
            await removeUserFromArchived(userData.id);

            setArchivedUsers((prevArchived) => prevArchived.filter((user) => user.id !== userData.id));
            setUsers((prevUsers) => ({
                ...prevUsers,
                data: [userData, ...prevUsers.data],
            }));
        } catch (error) {
            console.error(error);
        }
    };

    const handlePageChange = async (page: number) => {
        if (page < 1 || page > users.meta.pages) {
            return;
        }
        setCurrentPage(page);
        fetchUsers({
            ...filters,
            order_by: `${usersSort.columnId},${usersSort.direction}`,
            page_size: users.meta.page_size,
            page_no: page,
        });
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === "") {
            setCurrentPage("");
            return;
        }
        const newPage = Number(event.target.value);
        if (newPage > 0 && newPage <= users.meta.pages) {
            setCurrentPage(newPage);
        }
    };

    const handleOnBlur = () => {
        if (currentPage === "" || currentPage === users.meta.page_no) {
            setCurrentPage(users.meta.page_no);
            return;
        }
        if (Boolean(currentPage) && Number(currentPage) > 0 && Number(currentPage) <= users.meta.pages) {
            previousPageValue.current = currentPage;
            handlePageChange(Number(currentPage));
        }
    };

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            if (currentPage === users.meta.page_no) {
                return;
            }
            paginationTextInputRef.current.blur();
        }
        if (event.key === "Escape") {
            setCurrentPage(previousPageValue.current || 1);
            setTimeout(() => {
                paginationTextInputRef.current.blur();
            }, 1);
            return;
        }
    };

    const handleOpenTeamsModal = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        ev.preventDefault();
        const selectedUserIds = users.data.filter((user) => user.selectedForTeam).map((user) => user.id);

        if (selectedUserIds.length > 0) {
            setShouldOpenTeamsModal(true);
        }
    };

    const handleCloseTeamsModal = (reason: "cancel" | "confirm") => {
        if (reason === "cancel") {
            setUsers((prevState) => ({
                ...prevState,
                data: prevState.data.map((user) => ({
                    ...user,
                    selectedForTeams: false,
                })),
            }));
        }

        setShouldOpenTeamsModal(false);
    };

    const handleChipSelect = (selectedTeam: string) => {
        const newFilters = {
            ...filters,
            filter_teams: [selectedTeam],
        };
        setFilters(newFilters);
        fetchUsers({ ...newFilters, order_by: `${usersSort.columnId},${usersSort.direction}` });
    };

    const handleOpenInvitationModal = () => {
        setActiveTab(1);
        setInviteModalOpen(true);
        fetchPendingInvitations();
    };

    const handleInviteSelectedUsers = () => {
        setActiveTab(0);
        setInviteModalOpen(true);
    };

    const closeInviteModal = () => {
        setInviteModalOpen(false);
    };

    const handleConfirmAddTeam = async (userIds: number[], teamNames: string[]) => {
        dispatch(toggleLoader(true));
        try {
            const data = {
                user_ids: userIds,
                teams: teamNames,
            };

            await addNewTeam(data);

            setUsers((prevUsers) => {
                const updatedUsers = prevUsers.data.map((user) => {
                    if (userIds.includes(user.id)) {
                        const newTeams = teamNames
                            .filter((name) => !user.teams.some((team) => team.name === name))
                            .map((name) => ({ name }));

                        return {
                            ...user,
                            teams: [...user.teams, ...newTeams],
                        };
                    }

                    return user;
                });

                return { ...prevUsers, data: updatedUsers };
            });

            setTeamsData((prevTeams) => {
                const updatedTeams = teamNames
                    .filter((name) => !prevTeams.some((team) => team.name === name))
                    .map((name) => ({ id: name, name, selected: false }));

                return [...prevTeams, ...updatedTeams];
            });

            dispatch(
                setToastConfig({
                    title: intl.formatMessage(
                        { id: "userManagement.addUserToTeam.toast.title" },
                        { teamNames: teamNames.join(", ") }
                    ),
                    content: "",
                })
            );
        } catch (error) {
            console.error(error);
        } finally {
            dispatch(toggleLoader(false));
            setShouldOpenTeamsModal(false);
        }
    };

    const handleRemoveTeamFromUser = async (user_id: number, teamName: string) => {
        dispatch(toggleLoader(true));
        try {
            const data = { teams: [teamName] };

            await removeTeamFromUser(user_id, data);
            setUsers((prevUsers) => {
                const updatedUsers = prevUsers.data.map((user) =>
                    user.id === user_id
                        ? {
                              ...user,
                              teams: user.teams.filter((team) => team.name !== teamName),
                          }
                        : user
                );

                return { ...prevUsers, data: updatedUsers };
            });
        } catch (error) {
            console.error(error);
        } finally {
            dispatch(toggleLoader(false));
            const updatedUser = users.data.find((user) => user.id === user_id);
            if (updatedUser) {
                dispatch(
                    setToastConfig({
                        title: intl.formatMessage(
                            { id: "userManagement.romveUserFromTeam.toast.title" },
                            { userName: updatedUser.name, teamName: teamName }
                        ),
                        content: "",
                    })
                );
            }
        }
    };

    useEffect(() => {
        if (inviteModalOpen) {
            fetchPendingInvitations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteModalOpen]);

    useEffect(() => {
        const initialFetch = async () => {
            if (!orgId || !groupId) return;

            dispatch(toggleLoader(true));

            if (!teamsData.length) {
                fetchTeams();
            }

            if (canInviteUsers) {
                fetchPendingInvitations();
            }

            dispatch(fetchPermissionsRefThunk());
            dispatch(fetchPermissionsRefBundleThunk());

            try {
                const payloadParams = {
                    page_no: 1,
                    page_size: DEFAULT_PAGE_SIZE,
                    filter_enabled: true,
                    order_by: `${DEFAULT_SORT.columnId},${DEFAULT_SORT.direction}`,
                    only_user_org_selected: true,
                };

                const { data } = await getUsersManagement(payloadParams);

                setUsers({
                    ...users,
                    meta: data.meta,
                    data: convertUsers(data.results, groupId),
                    status: "done",
                });

                const { data: archivedResults } = await getUsersManagement({ ...payloadParams, filter_enabled: false });
                setArchivedUsers(convertUsers(archivedResults.results, groupId));
            } catch (error) {
                console.error(error);
            } finally {
                dispatch(toggleLoader(false));
            }
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --------------------------------------------------------------------------------------------------------------
    // Observe the selected client hub and create a list of user functions accordingly
    // --------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (isEmpty(permissionsBundle)) return;

        const rolesWithUpdatedPlaceholder = getUserRolesWithCustomPlaceholder();

        const ldPermission = getPermissionNameFromRolesList(permissionsBundle, selectedClientHub?.roles_ld || [], "LD");
        const pacePermission = getPermissionNameFromRolesList(
            permissionsBundle,
            selectedClientHub?.roles_pace || [],
            "PACE"
        );

        const userFunctions = getUserRolesByPermissions(
            { ldPermission, pacePermission },
            permissionsBundle,
            rolesWithUpdatedPlaceholder
        );

        setUserFunctions(userFunctions);
    }, [permissionsBundle, selectedClientHub]);

    return {
        users,
        archivedUsers,
        teamsData,
        userFunctions,
        filters,
        inputName,
        usersSort,
        paginationTextInputRef,
        tableHeader,
        hidableColumns,
        visibleColumns,
        disableAll,
        disableClearAll,
        shouldOpenArchiveModal,
        currentPage,
        shouldOpenTeamsModal,
        twoUsersSelected,
        areSomeUsersChecked,
        allUsersChecked,
        selectedUserName,
        activeTab,
        inviteModalOpen,
        pendingInvitations,
        orgId,
        fetchUsers,
        handleSort,
        handleChangeFilters,
        handleClearFilters,
        handleArchiveUser,
        handleSelectFunction,
        toggleCheckUser,
        toggleCheckAllUsers,
        handleToggleColumn,
        handleOpenArchiveModal,
        handleCloseArchiveModal,
        handleUnarchiveUser,
        handlePageChange,
        handleOpenTeamsModal,
        handleCloseTeamsModal,
        handleConfirmAddTeam,
        handleChipSelect,
        handleInputChange,
        handleOnBlur,
        handleOnKeyDown,
        setSelectedUserForTeam,
        handleOpenInvitationModal,
        handleInviteSelectedUsers,
        closeInviteModal,
        handleRemoveTeamFromUser,
    };
};

export default useUserManagement;
